// 以 id 對心智圖做局部修改。供 MCP 與日後的編輯器共用；不改動未指定的部分，保留使用者的調整。
import { type MindMap, type Topic, type Structure, STRUCTURES, hash32, indexTopics, walk } from './model.js';
import { ICON_NAMES } from './icons.js';
import { COLOR_NAMES, THEME_NAMES } from './theme.js';

export interface TopicAttrs {
  icon?: string | null;
  color?: string | null;
  boundary?: string | null;
  collapsed?: boolean;
  note?: string | null;
}

/** 嫁接用的主題樹（不含 id，套用時一律產生新 id）。 */
export interface TopicInput {
  title: string;
  attrs?: TopicAttrs;
  children?: TopicInput[];
}

/** 位置：parent + index，或相對兄弟 after／before（此時可省略 parent）。 */
interface Placement { parent?: string; index?: number; after?: string; before?: string }

export type MapOp =
  | ({ op: 'add'; title: string; attrs?: TopicAttrs; children?: string[] } & Placement)
  | { op: 'update'; id: string; title?: string; attrs?: TopicAttrs }
  | ({ op: 'move'; id: string } & Placement)
  | { op: 'insert_parent'; id: string; title: string; attrs?: TopicAttrs }
  | ({ op: 'graft'; topics: TopicInput[] } & Placement)
  | { op: 'delete'; id: string }
  | { op: 'relate'; from: string; to: string; label?: string }
  | { op: 'unrelate'; from: string; to: string }
  | { op: 'set_map'; structure?: Structure; theme?: string };

export class OpError extends Error {}

// 複製整張圖，避免部分失敗時留下半套修改。
function cloneMap(map: MindMap): MindMap {
  return structuredClone(map);
}

// 產生不與現有 id 衝突的新 id。
function newId(existing: Map<string, Topic>, seed: string): string {
  for (let attempt = 0; ; attempt++) {
    const id = 't' + hash32(`${seed}#${attempt}`).toString(36);
    if (!existing.has(id)) return id;
  }
}

// 套用屬性；null 代表移除該屬性。
function applyAttrs(topic: Topic, attrs: TopicAttrs | undefined, isRoot: boolean): void {
  if (!attrs) return;
  if (attrs.icon !== undefined) {
    if (attrs.icon === null) delete topic.icon;
    else if (!ICON_NAMES.includes(attrs.icon)) throw new OpError(`未知圖示「${attrs.icon}」，可用：${ICON_NAMES.join(', ')}`);
    else topic.icon = attrs.icon;
  }
  if (attrs.color !== undefined) {
    if (attrs.color === null) delete topic.color;
    else if (!COLOR_NAMES.includes(attrs.color) && !/^#[0-9a-f]{6}$/i.test(attrs.color)) throw new OpError(`未知顏色「${attrs.color}」`);
    else topic.color = attrs.color.toLowerCase();
  }
  if (attrs.boundary !== undefined) {
    if (attrs.boundary === null) delete topic.boundary;
    else if (isRoot) throw new OpError('中心主題不能加外框');
    else topic.boundary = attrs.boundary;
  }
  if (attrs.collapsed !== undefined) {
    if (attrs.collapsed) topic.collapsed = true; else delete topic.collapsed;
  }
  if (attrs.note !== undefined) {
    if (attrs.note === null || attrs.note === '') delete topic.note; else topic.note = attrs.note;
  }
}

// 檢查並整理標題。
function cleanNewTitle(title: string): string {
  const trimmed = title.replace(/\s+/g, ' ').trim();
  if (!trimmed) throw new OpError('標題不可為空');
  return trimmed;
}

// 依序套用操作；任何一步失敗就整批放棄（回傳錯誤，原圖不變）。回傳新圖與每步新增的 id。
export function applyOps(map: MindMap, ops: MapOp[]): { map: MindMap; created: string[] } {
  const next = cloneMap(map);
  const created: string[] = [];
  ops.forEach((operation, step) => {
    const index = indexTopics(next.root);
    const parents = new Map<string, Topic>();
    walk(next.root, (topic, _depth, parent) => { if (parent) parents.set(topic.id, parent); });
    const need = (id: string): Topic => {
      const topic = index.get(id);
      if (!topic) throw new OpError(`第 ${step + 1} 步：找不到 id「${id}」`);
      return topic;
    };
    // 解析插入位置：after／before 以兄弟為準，否則用 parent + index（預設最後）。
    const place = (placement: Placement): { parent: Topic; index: number } => {
      const sibling = placement.after ?? placement.before;
      if (sibling !== undefined) {
        const target = need(sibling);
        const parent = parents.get(target.id);
        if (!parent) throw new OpError('中心主題沒有同層主題');
        const at = parent.children.indexOf(target);
        return { parent, index: placement.after !== undefined ? at + 1 : at };
      }
      if (placement.parent === undefined) throw new OpError('需要 parent、after 或 before');
      const parent = need(placement.parent);
      const index = placement.index ?? parent.children.length;
      return { parent, index: Math.max(0, Math.min(index, parent.children.length)) };
    };
    // 依輸入樹建立主題（遞迴，全部新 id）。
    const build = (input: TopicInput, seed: string, added: string[]): Topic => {
      const topic: Topic = { id: newId(index, `${seed}/${input.title}/${index.size}`), title: cleanNewTitle(input.title), children: [], explicitId: true };
      index.set(topic.id, topic);
      applyAttrs(topic, input.attrs, false);
      added.push(topic.id);
      for (const child of input.children ?? []) topic.children.push(build(child, topic.id, added));
      return topic;
    };
    try {
      switch (operation.op) {
        case 'add': {
          const { parent, index: position } = place(operation);
          const topic: Topic = { id: newId(index, `${parent.id}/${operation.title}/${index.size}`), title: cleanNewTitle(operation.title), children: [], explicitId: true };
          index.set(topic.id, topic);
          applyAttrs(topic, operation.attrs, false);
          const added = [topic.id];
          for (const childTitle of operation.children ?? []) {
            const child: Topic = { id: newId(index, `${topic.id}/${childTitle}`), title: cleanNewTitle(childTitle), children: [], explicitId: true };
            index.set(child.id, child);
            topic.children.push(child);
            added.push(child.id);
          }
          parent.children.splice(position, 0, topic);
          created.push(...added);
          break;
        }
        case 'graft': {
          if (!operation.topics.length) throw new OpError('沒有要插入的主題');
          const { parent, index: position } = place(operation);
          const added: string[] = [];
          const topics = operation.topics.map(input => build(input, parent.id, added));
          parent.children.splice(position, 0, ...topics);
          created.push(...added);
          break;
        }
        case 'insert_parent': {
          const topic = need(operation.id);
          const parent = parents.get(topic.id);
          if (!parent) throw new OpError('中心主題不能插入父主題');
          const added: string[] = [];
          const inserted = build({ title: operation.title, ...(operation.attrs ? { attrs: operation.attrs } : {}) }, parent.id, added);
          parent.children.splice(parent.children.indexOf(topic), 1, inserted);
          inserted.children.push(topic);
          created.push(...added);
          break;
        }
        case 'update': {
          const topic = need(operation.id);
          if (operation.title !== undefined) topic.title = cleanNewTitle(operation.title);
          applyAttrs(topic, operation.attrs, topic === next.root);
          break;
        }
        case 'move': {
          const topic = need(operation.id);
          if (topic === next.root) throw new OpError('不能移動中心主題');
          if (operation.after === topic.id || operation.before === topic.id) break;
          const { parent, index: position } = place(operation);
          let cursor: Topic | undefined = parent;
          while (cursor) {
            if (cursor === topic) throw new OpError('不能把主題移到自己的子孫底下');
            cursor = parents.get(cursor.id);
          }
          const oldParent = parents.get(topic.id)!;
          const oldIndex = oldParent.children.indexOf(topic);
          oldParent.children.splice(oldIndex, 1);
          // 同一父主題內往後移時，移除後索引會少 1。
          const adjusted = oldParent === parent && oldIndex < position ? position - 1 : position;
          parent.children.splice(adjusted, 0, topic);
          break;
        }
        case 'delete': {
          const topic = need(operation.id);
          if (topic === next.root) throw new OpError('不能刪除中心主題');
          const parent = parents.get(topic.id)!;
          parent.children.splice(parent.children.indexOf(topic), 1);
          const removed = new Set<string>();
          walk(topic, item => removed.add(item.id));
          next.relations = next.relations.filter(relation => !removed.has(relation.from) && !removed.has(relation.to));
          break;
        }
        case 'relate': {
          need(operation.from);
          need(operation.to);
          if (operation.from === operation.to) throw new OpError('關聯線兩端不可相同');
          next.relations = next.relations.filter(relation => !(relation.from === operation.from && relation.to === operation.to));
          next.relations.push({ from: operation.from, to: operation.to, ...(operation.label ? { label: operation.label } : {}) });
          break;
        }
        case 'unrelate': {
          const before = next.relations.length;
          next.relations = next.relations.filter(relation => !(relation.from === operation.from && relation.to === operation.to));
          if (next.relations.length === before) throw new OpError(`找不到 ${operation.from} -> ${operation.to} 的關聯線`);
          break;
        }
        case 'set_map': {
          if (operation.structure !== undefined) {
            if (!(STRUCTURES as readonly string[]).includes(operation.structure)) throw new OpError(`未知結構「${operation.structure}」`);
            next.structure = operation.structure;
          }
          if (operation.theme !== undefined) {
            if (!THEME_NAMES.includes(operation.theme)) throw new OpError(`未知主題「${operation.theme}」`);
            next.theme = operation.theme;
          }
          break;
        }
        default:
          throw new OpError(`未知操作 ${(operation as { op: string }).op}`);
      }
    } catch (error) {
      if (error instanceof OpError && !error.message.startsWith('第 ')) throw new OpError(`第 ${step + 1} 步（${operation.op}）：${error.message}`);
      throw error;
    }
  });
  return { map: next, created };
}
